/**
 * Chamadas a API do TEG.
 *
 * Tres responsabilidades, e nenhuma tela precisa saber delas:
 *
 * 1. anexar o token de acesso que vive em memoria;
 * 2. renovar antes de mandar, quando ele esta perto de vencer, e uma vez depois
 *    de um 401 — janela de relogio desencontrado nao pode virar "entre de novo";
 * 3. traduzir a resposta de erro da API no contrato { situacao, mensagem,
 *    correlacao } para uma excecao com frase pronta para a tela.
 */

import { sessaoAtiva, tokenUtilizavel } from './armazem-token';
import { lerConfiguracao } from './configuracao';
import { renovar } from './fluxo-autorizacao';
import { mensagemDaApi, MENSAGEM_SEM_RESPOSTA } from './mensagens';

export class ErroApi extends Error {
  constructor(
    readonly situacao: number,
    readonly aviso: string,
    readonly correlacao: string | null = null,
  ) {
    super(aviso);
    this.name = 'ErroApi';
  }

  /** 401: a sessao nao vale mais. Entrar de novo resolve. */
  get pedeEntrarDeNovo(): boolean {
    return this.situacao === 401;
  }

  /** 403: autenticado, mas sem direito. Entrar de novo NAO resolve. */
  get pedeOutroCaminho(): boolean {
    return this.situacao === 403;
  }
}

/**
 * Renovacao com fila de um: varias chamadas simultaneas com token vencido
 * disparariam varias renovacoes, e o Keycloak invalida o token de renovacao
 * anterior a cada uso — a segunda renovacao mataria a sessao que a primeira
 * acabou de obter.
 */
let renovacaoEmCurso: Promise<boolean> | null = null;

export function renovarUmaVezSo(): Promise<boolean> {
  if (!renovacaoEmCurso) {
    renovacaoEmCurso = renovar().finally(() => {
      renovacaoEmCurso = null;
    });
  }
  return renovacaoEmCurso;
}

export interface OpcoesChamada {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /** Chamada que nao exige token — a de redefinicao de senha, por exemplo. */
  publica?: boolean;
  /** Sessões públicas opacas (ex.: candidato gov.br) usam cookie HttpOnly. */
  credenciais?: boolean;
  sinal?: AbortSignal;
}

/**
 * Onde a chamada vai. Com cookie (`credenciais`), vai para a PRÓPRIA origem do
 * site, que repassa `/bff/*` para a API: o `vercel.json` publicado ganha o
 * repasse na esteira (`scripts/repasse-da-api.mjs`) e o Vite faz o mesmo em
 * desenvolvimento. Front e API moram em sites diferentes (Vercel e Render), e
 * o navegador não guarda nem devolve cookie `SameSite=Strict` entre sites: sem
 * o repasse, a sessão do inscrito não passa do primeiro passo.
 *
 * O resto, com token no cabeçalho, continua indo direto à API.
 *
 * Provisório: a saída definitiva é a API ganhar endereço no mesmo domínio do
 * site (ver "Dívida registrada: sessão do inscrito pelo repasse do site" em
 * docs/ALVOS.md); aí esta função volta a ser só `${api}${caminho}`.
 */
export function enderecoDaChamada(caminho: string, credenciais: boolean, api: string): string {
  return credenciais && caminho.startsWith('/bff/') ? caminho : `${api}${caminho}`;
}

export async function chamarApi<T>(caminho: string, opcoes: OpcoesChamada = {}): Promise<T> {
  const configuracao = lerConfiguracao();
  const endereco = enderecoDaChamada(caminho, opcoes.credenciais === true, configuracao.api);

  if (!opcoes.publica && !tokenUtilizavel()) {
    await renovarUmaVezSo();
  }

  let resposta = await enviar(endereco, opcoes);

  // Um 401 pode ser token vencido por alguns segundos. Renova e tenta de novo,
  // uma vez. Duas tentativas viram laco.
  if (resposta.status === 401 && !opcoes.publica) {
    if (await renovarUmaVezSo()) {
      resposta = await enviar(endereco, opcoes);
    }
  }

  if (resposta.status === 204) {
    return undefined as T;
  }

  const corpo = await lerCorpo(resposta);

  if (!resposta.ok) {
    throw new ErroApi(
      resposta.status,
      mensagemDaApi(resposta.status, corpo),
      resposta.headers.get('x-correlation-id'),
    );
  }

  return corpo as T;
}

/**
 * Baixa um arquivo da API (planilha, PDF) com a mesma autorização e a mesma
 * renovação de `chamarApi`. A resposta não é JSON: volta o conteúdo como
 * `Blob`. Erro vem como `ErroApi`, com a frase pronta para a tela.
 *
 * O nome do arquivo fica com a tela: ler `Content-Disposition` de outra
 * origem exigiria expor o cabeçalho no CORS da API.
 */
export async function baixarDaApi(caminho: string): Promise<Blob> {
  const configuracao = lerConfiguracao();
  const endereco = `${configuracao.api}${caminho}`;

  if (!tokenUtilizavel()) {
    await renovarUmaVezSo();
  }

  let resposta = await enviar(endereco, {});
  if (resposta.status === 401 && (await renovarUmaVezSo())) {
    resposta = await enviar(endereco, {});
  }

  if (!resposta.ok) {
    throw new ErroApi(
      resposta.status,
      mensagemDaApi(resposta.status, await lerCorpo(resposta)),
      resposta.headers.get('x-correlation-id'),
    );
  }
  return resposta.blob();
}

async function enviar(endereco: string, opcoes: OpcoesChamada): Promise<Response> {
  const cabecalhos: Record<string, string> = { Accept: 'application/json' };

  if (!opcoes.publica) {
    const sessao = sessaoAtiva();
    if (sessao) {
      cabecalhos.Authorization = `Bearer ${sessao.acesso}`;
    }
  }

  if (opcoes.corpo !== undefined) {
    cabecalhos['Content-Type'] = 'application/json';
  }

  try {
    return await fetch(endereco, {
      method: opcoes.metodo ?? 'GET',
      headers: cabecalhos,
      body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
      // A API nao usa cookie: a autorizacao vai no cabecalho, e o token vive em
      // memoria. Mandar credencial abriria porta que nao precisa estar aberta.
      credentials: opcoes.credenciais ? 'include' : 'omit',
      signal: opcoes.sinal ?? null,
    });
  } catch (erro) {
    // Rede fora, servico fora, CORS recusado: nao ha resposta para ler. O
    // motivo tecnico fica no console; a tela recebe a frase.
    console.warn('Chamada à API não obteve resposta.', erro);
    throw new ErroApi(0, MENSAGEM_SEM_RESPOSTA, null);
  }
}

async function lerCorpo(resposta: Response): Promise<unknown> {
  const tipo = resposta.headers.get('content-type') ?? '';

  if (!tipo.includes('json')) {
    return null;
  }

  try {
    return await resposta.json();
  } catch {
    return null;
  }
}
